import { useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { messageOf } from '@/api/errors';
import { cx } from '@/lib/cx';
import { plural } from '@/lib/format';
import { Button } from './Button';
import { ConfirmDialog } from './Modal';
import { toast } from './Toast';
import s from './Selection.module.css';

/**
 * Picking several rows to act on at once, the web form of the phone's long-press multi-select
 * (`frontend/lib/widgets/multi_select.dart`). One model for every list and table:
 *
 * - A checkbox per row, shown on hover and on every row once anything is picked.
 * - Shift-click picks the run between the last row clicked and this one, in the order on screen.
 * - The header box picks **every row on screen** — never rows a filter hides or pages not loaded,
 *   so a click cannot delete history the user never saw (the phone's rule for its paged lists).
 * - Esc clears. What a filter hides stops counting as picked, so a delete acts only on what is shown.
 */
export interface Selection {
  /** Picked ids that are still on screen. */
  ids: string[];
  count: number;
  has: (id: string) => boolean;
  /** Toggle one row; with `range`, pick every row between the last one clicked and this one. */
  toggle: (id: string, range: boolean, order: string[]) => void;
  /** Pick all of `order` (the rows on screen), or clear them when all are already picked. */
  toggleAll: (order: string[]) => void;
  clear: () => void;
}

export function useSelection(visible: string[]): Selection {
  const [picked, setPicked] = useState<ReadonlySet<string>>(() => new Set());
  const anchor = useRef<string | null>(null);
  const ids = useMemo(() => visible.filter((id) => picked.has(id)), [visible, picked]);
  const count = ids.length;

  useEffect(() => {
    if (count === 0) return;
    const onKey = (e: KeyboardEvent) => {
      // A dialog's own Esc closes the dialog; the selection survives it.
      if (e.key !== 'Escape' || document.querySelector('[role="dialog"]')) return;
      setPicked(new Set());
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [count]);

  return {
    ids,
    count,
    has: (id) => picked.has(id),
    toggle: (id, range, order) => {
      const next = new Set(picked);
      const from = anchor.current ? order.indexOf(anchor.current) : -1;
      const to = order.indexOf(id);
      if (range && from >= 0 && to >= 0) {
        const on = !picked.has(id);
        for (const rowId of order.slice(Math.min(from, to), Math.max(from, to) + 1)) {
          if (on) next.add(rowId);
          else next.delete(rowId);
        }
      } else if (next.has(id)) next.delete(id);
      else next.add(id);
      anchor.current = id;
      setPicked(next);
    },
    toggleAll: (order) => {
      const all = order.length > 0 && order.every((id) => picked.has(id));
      setPicked(all ? new Set() : new Set(order));
    },
    clear: () => setPicked(new Set()),
  };
}

/** A row's checkbox. Clicks stop here, so picking a row never also opens it. */
export function SelectBox({
  checked,
  indeterminate,
  label,
  onToggle,
  className,
}: {
  checked: boolean;
  indeterminate?: boolean;
  label: string;
  onToggle: (shift: boolean) => void;
  className?: string;
}) {
  return (
    <input
      type="checkbox"
      className={cx(s.box, className)}
      aria-label={label}
      checked={checked}
      ref={(el) => {
        if (el) el.indeterminate = Boolean(indeterminate);
      }}
      onClick={(e: MouseEvent<HTMLInputElement>) => {
        e.stopPropagation();
        onToggle(e.shiftKey);
      }}
      onKeyDown={(e) => e.stopPropagation()}
      onChange={() => undefined}
    />
  );
}

/**
 * Takes the place of a list's toolbar while rows are picked: how many, the actions, and Clear.
 * `noun` is the thing in the singular and plural ("strategy", "strategies").
 */
export function SelectionBar({
  count,
  noun,
  onClear,
  children,
  className,
}: {
  count: number;
  noun: [string, string];
  onClear: () => void;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cx(s.bar, className)} role="toolbar" aria-label={`${count} selected`}>
      <span className={s.count}>
        {count} {count === 1 ? noun[0] : noun[1]} selected
      </span>
      <span className={s.grow} />
      {children}
      <Button size="sm" variant="ghost" onClick={onClear}>
        Clear
      </Button>
    </div>
  );
}

/** The most ids any bulk delete endpoint accepts in one request (most cap at 200). */
const BATCH = 200;

/**
 * Delete the picked rows behind one confirmation: one request per 200 ids, then a toast with the
 * server's own count. Ids already gone are skipped by the server rather than refused, so a row
 * deleted on another device meanwhile does not fail the batch.
 *
 * Returns the button that opens the confirmation and the dialog to render.
 */
export function useBulkDelete({
  ids,
  noun,
  remove,
  consequence,
  onDone,
  verb = ['Delete', 'Deleted'],
  from,
}: {
  ids: string[];
  noun: [string, string];
  remove: (ids: string[]) => Promise<unknown>;
  /** What else goes with them, said in the confirmation ("Alerts already sent are kept."). */
  consequence?: ReactNode;
  onDone?: () => void;
  /** For taking things off a list rather than deleting them: `['Remove', 'Removed']`. */
  verb?: [string, string];
  /** What they are taken off, for the title: "Remove 3 instruments from Majors?". */
  from?: string;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const name = (n: number) => (n === 1 ? noun[0] : noun[1]);

  const confirm = async () => {
    setBusy(true);
    let deleted = 0;
    try {
      for (let i = 0; i < ids.length; i += BATCH) {
        // Endpoints report a count (`deleted`) or the ids that went (`removed`); either is what
        // really changed, which can be fewer than asked for.
        const out = (await remove(ids.slice(i, i + BATCH))) as { deleted?: number; removed?: unknown[] } | undefined;
        deleted += out?.deleted ?? out?.removed?.length ?? Math.min(BATCH, ids.length - i);
      }
      toast.success(`${verb[1]} ${plural(deleted, noun[0], noun[1])}`);
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  return {
    button: (
      <Button size="sm" variant="danger" icon={verb[0] === 'Delete' ? 'delete' : 'remove_circle_outline'} onClick={() => setOpen(true)} disabled={ids.length === 0}>
        {verb[0]}
      </Button>
    ),
    dialog: (
      <ConfirmDialog
        open={open}
        onClose={() => setOpen(false)}
        onConfirm={() => void confirm()}
        title={`${verb[0]} ${ids.length} ${name(ids.length)}${from ? ` from ${from}` : ''}?`}
        confirmLabel={`${verb[0]} ${ids.length} ${name(ids.length)}`}
        danger
        busy={busy}
      >
        {consequence}
        {verb[0] === 'Delete' ? `${consequence ? ' ' : ''}This cannot be undone.` : null}
      </ConfirmDialog>
    ),
  };
}
